import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authApi } from '../lib/api';
import { tokens } from '../lib/tokens';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    if (!tokens.isAuthenticated) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await authApi.me();
      setUser(me);
    } catch {
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Redirect to a provider's OAuth flow (top-level navigation).
  const login = useCallback((provider) => {
    window.location.href = authApi.loginURL(provider);
  }, []);

  // Called by the OAuth callback page with tokens from the URL fragment.
  const completeLogin = useCallback(
    async ({ accessToken, refreshToken }) => {
      tokens.set({ accessToken, refreshToken });
      setLoading(true);
      await loadMe();
    },
    [loadMe]
  );

  // Local-dev only: backend issues guest tokens (404 elsewhere).
  const loginAsGuest = useCallback(async () => {
    const data = await authApi.guestLogin();
    await completeLogin({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });
  }, [completeLogin]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    tokens.clear();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      loading,
      login,
      loginAsGuest,
      logout,
      completeLogin,
    }),
    [user, loading, login, loginAsGuest, logout, completeLogin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
